import { graphql, useStaticQuery } from 'gatsby'
import { createContext, useContext } from 'react'
import { orderBy } from 'lodash'

type SmartThingsCatalogCategoryResult = {
  _id: number
  localizations: {
    locale: string
    displayName: string
  }[]
  brandsIds: number[]
  brands: {
    _id: number
    name: string
    iconUrl: string
    countries: { code: string; name: string }[]
  }[]
}

type HydratedSmartThingsCatalogCategory = SmartThingsCatalogCategoryResult & {
  displayName: string
}

type State = {
  categoriesIds: number[]
  categories: Record<number, HydratedSmartThingsCatalogCategory>
  allCategories: HydratedSmartThingsCatalogCategory[]
  brandCountryCodes: Set<string>
}

export type CatalogContextValue = {
  state: State
}

export const CatalogContext = createContext<CatalogContextValue | undefined>(
  undefined
)

export const CatalogProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const data = useStaticQuery(graphql`
    query AllSmartThingsCatalogCategory {
      allSmartThingsCatalogCategory {
        nodes {
          _id
          localizations {
            locale
            displayName
          }
          brandsIds
          brands {
            _id
            name
            iconUrl
            countries {
              code
              name
            }
          }
        }
      }
    }
  `)

  const categories = data?.allSmartThingsCatalogCategory?.nodes ?? []
  const state = getUseCatalogState(categories)

  return (
    <CatalogContext.Provider value={{ state }}>
      {children}
    </CatalogContext.Provider>
  )
}

export const useCatalog = (): CatalogContextValue => {
  const context = useContext(CatalogContext)

  if (context === undefined) {
    throw new Error('useCatalog must be used within a CatalogContext')
  }

  return context
}

export const getUseCatalogState = (
  categories: SmartThingsCatalogCategoryResult[]
): State => {
  let allCategories = categories.map(category => ({
    ...category,
    displayName:
      category?.localizations?.find(
        l => l?.locale?.toLocaleLowerCase() === 'en-us'
      )?.displayName ?? '',
  }))

  allCategories = orderBy(allCategories, ['displayName'], ['asc'])

  const categoriesIds = allCategories.map(({ _id }) => _id as number)
  const categoriesById = allCategories.reduce(
    (acc, category) => ({ ...acc, [category._id as number]: category }),
    {}
  )

  // List of all country codes associated with brands/products
  const brandCountryCodes = allCategories.reduce((acc, category) => {
    category.brands.forEach(brand =>
      brand.countries.forEach(country => acc.add(country.code))
    )
    return acc
  }, new Set()) as Set<string>

  return {
    categoriesIds,
    categories: categoriesById,
    allCategories,
    brandCountryCodes,
  }
}
